import { AxiosError } from "axios";
import { type ClassValue, clsx } from "clsx";
import { FC } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Retrieves a cookie by name.
 * @param name The cookie name.
 * @returns The cookie value or undefined.
 */
export const getCookie = (name: string): string | undefined => {
  if (typeof window !== "undefined") {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const part = parts.pop();
      if (part) return part.split(";").shift();
    }
  }
  return undefined;
};

/**
 * Retrieves a bearer token from cookies or environment variables.
 * @param key The cookie key.
 * @returns The bearer token.
 */
export const getBearerTokenFromCookie = (key: string) => {
  const token = typeof window !== "undefined" ? getCookie(key) : null;
  return token;
};

/**
 * Handles Axios errors and logs appropriate messages.
 * @param error The Axios error object.
 * @returns An object with status and message or null.
 */
export const handleApiError = (error: AxiosError) => {
  if (error.response) {
    const { status, data } = error.response as {
      status: number;
      data: {
        statusText: string;
        data: {
          message?: string;
          msg: string;
        };
        error?: {
          message?: string;
        };
      };
    };

    console.error(
      `Status: ${status}, Message: ${
        data?.data?.msg ||
        data?.data?.message ||
        data?.statusText ||
        data?.error?.message ||
        "Unknown error"
      }`
    );

    return {
      status,
      message:
        data?.data?.msg ||
        data?.data?.message ||
        data?.error?.message ||
        "Unknown error",
    };
  } else if (error.request) {
    console.error("No response received:", error.request);
  } else {
    console.error("Error setting up request:", error.message);
  }

  return null;
};

/**
 * Sets a cookie with specified parameters.
 * @param name The cookie name.
 * @param value The cookie value.
 * @param maxAge The maximum age of the cookie (default 3600 seconds).
 * @returns True upon success.
 */
export const setCookie = ({
  name,
  value,
  maxAge = 3600,
}: {
  name: string;
  value: string;
  maxAge?: number;
}) => {
  document.cookie = `${name}=; path=/; max-age=0; secure; samesite=strict`;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; secure; samesite=strict`;
  return true;
};

/* signInSchema, signUpSchema, getLeadEmailFieldError and isLeadEmailFieldValid
   moved to `@/lib/validation` so `cn()` no longer drags the Zod runtime into
   the shared chunk of every page. */

// Types for validation functions
interface FieldConfig {
  fieldName: string;
  label?: string;
  value?: any;
  valueType?: string;
  filedType: string;
  placeholder?: string;
  required?: boolean;
  validation?: (value: any, formData?: Record<string, any>) => string | null;
  [key: string]: any;
}

interface ValidationResult {
  errors: Record<string, boolean>;
  messages: Record<string, string>;
  hasErrors: boolean;
}

/**
 * Validates banner fields (like slide_frequency, status)
 * @param bannerFieldsConfig Array of field configurations
 * @param bannerFields Current banner field values
 * @returns Validation result with errors and messages
 */
export const validateFields = (
  bannerFieldsConfig: FieldConfig[],
  bannerFields: Record<string, any>
): ValidationResult => {
  const errors: Record<string, boolean> = {};
  const messages: Record<string, string> = {};
  let hasErrors = false;

  bannerFieldsConfig.forEach((field) => {
    if (field.filedType === "TEXT_BLOCK") return;

    const value = bannerFields[field.fieldName];

    // Check custom validation first (receives value and entire formData)
    if (field.validation) {
      const validationError = field.validation(value, bannerFields);
      if (validationError) {
        errors[field.fieldName] = true;
        messages[field.fieldName] = validationError;
        hasErrors = true;
        return; // Skip required check if custom validation failed
      }
    }

    if (field.required) {
      if (field.valueType === "number") {
        if (!value || value === "" || isNaN(Number(value))) {
          errors[field.fieldName] = true;
          messages[
            field.fieldName
          ] = `${field.label} is required and must be a valid number`;
          hasErrors = true;
        }
      } else if (field.valueType === "boolean") {
        if (value === null || value === undefined) {
          errors[field.fieldName] = true;
          messages[field.fieldName] = `${field.label} is required`;
          hasErrors = true;
        }
      } else {
        if (!value || (typeof value === "string" && value.trim() === "")) {
          errors[field.fieldName] = true;
          messages[field.fieldName] = `${field.label} is required`;
          hasErrors = true;
        }
      }
    }
  });

  return { errors, messages, hasErrors };
};

/**
 * Validates table data (metadata fields)
 * @param tableData Array of table row data
 * @param metadataFields Array of field configurations
 * @returns Validation result with errors and messages
 */
export const validateTableData = (
  metadataFields: FieldConfig[],
  tableData: Record<string, any>[]
): ValidationResult => {
  const errors: Record<string, boolean> = {};
  const messages: Record<string, string> = {};
  let hasErrors = false;

  tableData.forEach((rowData, rowIndex) => {
    const rowKey =
      rowData.id != null && String(rowData.id) !== ""
        ? String(rowData.id)
        : rowData.meta_id != null && String(rowData.meta_id) !== ""
          ? String(rowData.meta_id)
          : `row-${rowIndex}`;
    metadataFields.forEach((field) => {
      const value = rowData[field.fieldName as keyof typeof rowData];
      const errorKey = `${rowKey}_${field.fieldName}`;

      // Custom validation runs whether or not the field is required (an
      // optional field can still have shape rules once filled), and receives
      // the whole row so cross-field rules (e.g. "lines depend on the selected
      // format") are expressible.
      if (field.validation) {
        const validationResult = field.validation(value, rowData);
        if (validationResult) {
          errors[errorKey] = true;
          messages[errorKey] = validationResult;
          hasErrors = true;
        }
      } else if (field.required) {
          // Handle different types properly
          let isInvalid = false;

          if (field.valueType === "number") {
            // For numbers, check if undefined, null, empty string, or NaN
            isInvalid =
              value === undefined ||
              value === null ||
              value === "" ||
              isNaN(Number(value));
          } else if (field.valueType === "boolean") {
            // For booleans, only invalid if undefined or null
            isInvalid = value === undefined || value === null;
          } else {
            // For strings and other types
            isInvalid =
              value === undefined ||
              value === null ||
              (typeof value === "string" && value.trim() === "");
          }

          if (isInvalid) {
            errors[errorKey] = true;
            messages[errorKey] = `${
              field.header || field.fieldName
            } is required`;
            hasErrors = true;
          }
      }
    });
  });

  return { errors, messages, hasErrors };
};

export const shouldRenderSection = (sectionData: any, Component: FC | null) => {
  if (!Component || !sectionData || !Object.keys(sectionData).length)
    return false;

  // // Skip if status is explicitly "0" or false
  // if (sectionData["status"] === "0" || sectionData["status"] === false)
  //   return false;

  // // Render if status is:
  // // - "1" (string)
  // // - true (boolean)
  // // - undefined
  // // - null
  // return (
  //   sectionData["status"] === "1" ||
  //   sectionData["status"] === true ||
  //   sectionData["status"] === undefined ||
  //   sectionData["status"] === null
  // );

  return true;
};

/** IIT KGP CMS: `{highlight}` or `{{line}}` → wrapped spans for section titles */
export const formatSectionTitle = (
  title: string,
  className: string = "text-primary"
) => {
  if (!title) return "";

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, `<span class='${className}'>$1</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, `<span class='${className}'>`)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return title;
};

/** Guard for a value that gets written straight into a hand-built style attr. */
const SAFE_CSS_COLOR =
  /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/]+\)|[a-z]+)$/i;

/**
 * Same `{highlight}` / `{{line}}` markup as {@link formatSectionTitle}, but the
 * highlight color comes from the CMS, so it is applied as an inline style
 * instead of a fixed class. Falls back to `fallbackColor` when the CMS value is
 * missing or is not a plain CSS color.
 */
export const formatSectionTitleWithColor = (
  title: string,
  color: string | undefined,
  fallbackColor: string
) => {
  if (!title) return "";

  const safeColor =
    typeof color === "string" && SAFE_CSS_COLOR.test(color.trim())
      ? color.trim()
      : fallbackColor;

  const open = `<span style="color:${safeColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, `${open}$1</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title
      .replace(/\{/g, open)
      .replace(/\}/g, "</span>")
      .replace(/\s+/g, " ");
  }

  return title;
};

/**
 * Reads a nullable boolean column that means "on unless explicitly turned off"
 * (e.g. `page.chatbot_enabled`): NULL / undefined / missing → true.
 *
 * A plain `value !== false` test is WRONG here. The page repositories query with
 * `raw: true`, which skips Sequelize's model-level type parsing, so a MySQL
 * `tinyint(1)` arrives as the NUMBER 0 or 1 — and `0 !== false` is true, i.e. an
 * explicit "off" would read back as "on". `!!value` is equally wrong the other
 * way: it collapses NULL into off, losing the default-on meaning.
 */
export const isEnabledUnlessExplicitlyOff = (value: unknown): boolean =>
  !(value === false || value === 0 || value === "0" || value === "false");
